import { useCallback, useEffect, useContext } from "react";

import { FieldType } from "@regal-voice/shared-types";
import { Select } from "antd";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import set from "lodash/set";
import { useDispatch } from "react-redux";

import { RVFormFloatingLabel, RVFormItem } from "@regal-voice/ui";
import { JourneyFormContext } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/Contexts/JourneyFormContext";
import { setFields } from "Services/state/journeys/JourneyStateSlice";

import { unflattenFields } from "../../../Utils/utils";
import { ConditionalNodeOperator } from "../ConditionalNodeOperators";
import { getConditionalFormFieldValue } from "./helpers";

import type { EventSchemaOptionEntry } from "ServerData/EventSchema/eventSchemaTypes";

import styles from "../ConditionalFormItems.module.scss";

export const OPERATORS_WITHOUT_VALUE = ["isSet", "isNotSet", "isNotNull"];

export function ConditionalNodePropertyOperator({
    formValues,
    isContactCondtional,
    isEventConditional,
    propertyOperator,
    rootField,
    availableOperators,
    eventSchema,
    contactAttributes,
}: {
    formValues: any;
    isContactCondtional: boolean;
    isEventConditional: boolean;
    propertyOperator: string;
    rootField?: any;
    availableOperators: ConditionalNodeOperator[];
    eventSchema: EventSchemaOptionEntry;
    contactAttributes: any;
}): JSX.Element {
    const dispatch = useDispatch();
    // why do we need to assert this?
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { form } = useContext(JourneyFormContext)!;

    const updatePropertyTypeCallBack = useCallback(
        (propertyType: FieldType | undefined, propertyFieldId: string | undefined) => {
            let displayedOperator = "";
            if (availableOperators?.length > 0) {
                displayedOperator =
                    availableOperators.find((elem) => elem.value === propertyOperator)?.value || "isSet";
            }

            // within a list we need to pass
            if (rootField) {
                const filters = [...formValues.filters];
                filters.splice(rootField.name, 1, {
                    ...(!OPERATORS_WITHOUT_VALUE.includes(displayedOperator) ? { ...filters[rootField.name] } : {}),
                    propertyName: filters[rootField.name].propertyName,
                    propertyType,
                    propertyFieldId,
                    propertyOperator: displayedOperator,
                });

                dispatch(
                    setFields(
                        unflattenFields({
                            ...formValues,
                            filters,
                        })
                    )
                );
                return;
            }
            dispatch(
                setFields(
                    unflattenFields({
                        ...formValues,
                        propertyType,
                        propertyFieldId,
                        propertyOperator: displayedOperator,
                    })
                )
            );
        },
        [availableOperators, formValues]
    );

    useEffect(() => {
        if (!availableOperators?.length || !isEventConditional || isEmpty(eventSchema)) {
            return;
        }
        const propertyName = getConditionalFormFieldValue(formValues, "propertyName", rootField);
        updatePropertyTypeCallBack(eventSchema?.[propertyName]?.fieldType, eventSchema?.[propertyName]?.fieldId);
    }, [availableOperators, isEventConditional, propertyOperator, eventSchema]);

    useEffect(() => {
        if (!availableOperators?.length || !isContactCondtional || isEmpty(contactAttributes)) {
            return;
        }
        const propertyName = rootField
            ? getConditionalFormFieldValue(formValues, "propertyName", rootField)
            : getConditionalFormFieldValue(formValues, "contactAttribute", rootField);
        updatePropertyTypeCallBack(
            contactAttributes?.[propertyName]?.fieldType,
            contactAttributes?.[propertyName]?.fieldId
        );
    }, [availableOperators, isContactCondtional, propertyOperator, contactAttributes]);

    function handleOnChange(operator: string) {
        if (form) {
            if (operator === "isOneOf" || OPERATORS_WITHOUT_VALUE.includes(operator)) {
                // update propertyValueType when operator changes
                const formValuesCloned = cloneDeep(form.getFieldsValue());
                set(formValuesCloned, ["filters", rootField.name, "propertyValueType"], "literal");
                form.setFieldsValue(formValuesCloned);
            }
            if (
                (operator === "isOneOf" &&
                    ["contains", "notEquals", "equals", "startsWith"].includes(propertyOperator)) ||
                (propertyOperator === "isOneOf" && ["contains", "notEquals", "equals", "startsWith"].includes(operator))
            ) {
                // reset propertyValue when:
                // operator changes from "isOneOf" to one of ["equals", "notEquals", "contains"]
                // operator changes from one of ["equals", "notEquals", "contains"] to "isOneOf"
                const formValuesCloned = cloneDeep(form.getFieldsValue());
                set(formValuesCloned, ["filters", rootField.name, "propertyValue"], undefined);
                form.setFieldsValue(formValuesCloned);
            }
        }
    }

    return (
        <RVFormFloatingLabel
            {...(rootField ? { name: ["filters", rootField.name, "propertyOperator"] } : { name: "propertyOperator" })}
            key={`floating-propertyOperator-${rootField ? rootField.name : 1}`}
        >
            <RVFormItem
                {...(rootField
                    ? { ...rootField, name: [rootField.name, "propertyOperator"] }
                    : { name: "propertyOperator" })}
                key={`propertyOperator-${rootField ? rootField.name : 1}`}
                className={styles.formItem}
                initialValue="isSet"
                rules={[{ required: true, message: "Operator is required." }]}
                label={rootField ? `Operator` : undefined}
            >
                <Select
                    placeholder={rootField ? undefined : "Operator"}
                    onChange={handleOnChange}
                    data-testid={`select-${rootField.name}-propertyOperator`}
                >
                    {!availableOperators.length && (
                        <>
                            <Select.Option value="isNotSet">Is Not Set</Select.Option>
                            <Select.Option value="isSet">Is Set</Select.Option>
                        </>
                    )}
                    {availableOperators.map((x) => (
                        <Select.Option value={x.value} key={x.value}>
                            {x.name}
                        </Select.Option>
                    ))}
                </Select>
            </RVFormItem>
        </RVFormFloatingLabel>
    );
}
